<template>
  <!--
    A photograph or a clip, full screen, with the rest of the set one swipe away.

    ── Why this is a platform component ───────────────────────────────────────

    Because the alternative is a link. Every screen in the ERP that showed
    attachments showed thumbnails that opened the file in another tab, and on a
    phone another tab is leaving the application: the browser chrome comes back,
    the form is behind two taps, and going through six photographs of a job is
    six round trips out and back.

    It is also the only place video playback is set up correctly. A <video> that
    is going to seek needs range answers from the server (`/files/:id` gives
    them) and `playsinline`, without which iOS takes the clip full screen in its
    own player and hands back a page scrolled somewhere else.

    ── What it does not do ────────────────────────────────────────────────────

    It does not fetch, and it does not know what a file id is. It is handed a
    list of things that already have a URL, and it draws one of them.
  -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="items.length"
        ref="frame"
        class="lb"
        role="dialog"
        aria-modal="true"
        :aria-label="current?.filename || uiLabel('viewerTitle')"
        tabindex="-1"
        @keydown="onKeydown"
        @click.self="close"
      >
        <header class="lb__bar">
          <span class="lb__name">{{ current?.filename || '' }}</span>
          <span v-if="items.length > 1" class="lb__count">{{ index + 1 }} / {{ items.length }}</span>
          <button type="button" class="lb__icon" :aria-label="uiLabel('close')" @click="close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div class="lb__stage" @touchstart.passive="onTouchStart" @touchend.passive="onTouchEnd">
          <img
            v-if="kind === 'photo'"
            :key="current.url"
            :src="current.url"
            :alt="current.filename || ''"
            class="lb__media"
          />
          <!--
            `key` on the URL, or Vue keeps the same element across a next/prev
            and the browser goes on playing the previous clip's audio over the
            new one's picture.
          -->
          <video
            v-else-if="kind === 'video'"
            :key="current.url"
            :src="current.url"
            class="lb__media"
            controls
            playsinline
            preload="metadata"
          />
          <!-- Anything else: it can be fetched, it just cannot be shown here. -->
          <a v-else :href="current.url" class="lb__fallback" target="_blank" rel="noopener">
            {{ uiLabel('viewerDownload') }}
          </a>
        </div>

        <template v-if="items.length > 1">
          <button type="button" class="lb__step lb__step--back" :aria-label="uiLabel('viewerPrevious')" @click="step(-1)">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button type="button" class="lb__step lb__step--on" :aria-label="uiLabel('viewerNext')" @click="step(1)">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </template>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import { mediaKind } from '../utils/media'
import { uiLabel } from '../utils/labels'

const props = defineProps({
  /**
   * What can be looked at: `{ url, mimeType, filename }`. The caller decides
   * what goes in — every attachment of a parte, only the photographs of one
   * task — and the order is the order they are stepped through.
   */
  items: { type: Array, default: () => [] },
  /** Which one is showing. Bound, so the caller opens on the one that was tapped. */
  modelValue: { type: Number, default: 0 },
})

const emit = defineEmits(['update:modelValue', 'close'])

const frame = ref(null)
const index = ref(0)

watch(() => props.modelValue, (value) => { index.value = clamp(value) }, { immediate: true })
// A list that shrinks under it — an attachment deleted from the screen behind —
// must not leave this pointing past the end.
watch(() => props.items.length, () => { index.value = clamp(index.value) })

function clamp(value) {
  if (!props.items.length) return 0
  return Math.min(Math.max(0, Number(value) || 0), props.items.length - 1)
}

const current = computed(() => props.items[index.value] ?? null)
const kind = computed(() => mediaKind(current.value?.mimeType))

function step(delta) {
  if (!props.items.length) return
  // Wraps: going back from the first lands on the last. Six photographs of a
  // job are looked at in a loop, not as a list with two dead ends.
  const next = (index.value + delta + props.items.length) % props.items.length
  index.value = next
  emit('update:modelValue', next)
}

function close() {
  emit('close')
}

function onKeydown(event) {
  if (event.key === 'Escape') return close()
  if (event.key === 'ArrowRight') return step(1)
  if (event.key === 'ArrowLeft') return step(-1)
}

// ── The swipe ────────────────────────────────────────────────────────────────
//
// Deliberately crude, and deliberately not a library. Two numbers and a
// threshold: a horizontal move of more than 50px that is also more horizontal
// than vertical. The vertical comparison is what keeps a scroll from counting
// as a swipe on a tall photograph.
let touchX = 0
let touchY = 0

function onTouchStart(event) {
  touchX = event.changedTouches[0]?.clientX ?? 0
  touchY = event.changedTouches[0]?.clientY ?? 0
}

function onTouchEnd(event) {
  const dx = (event.changedTouches[0]?.clientX ?? 0) - touchX
  const dy = (event.changedTouches[0]?.clientY ?? 0) - touchY
  if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return
  step(dx < 0 ? 1 : -1)
}

onMounted(async () => {
  // The page behind must not scroll while this is up, and the frame has to have
  // focus or the arrow keys go to whatever was focused before it opened.
  document.body.style.overflow = 'hidden'
  await nextTick()
  frame.value?.focus()
})

onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<style scoped>
.lb {
  position: fixed;
  inset: 0;
  /* Lo que se ve (ui-kit utils/viewport.js): abierto con el teclado fuera, la
     barra con «cerrar» se iba por encima del borde de la pantalla. */
  top: var(--viewport-top, 0px);
  bottom: auto;
  height: var(--app-height, 100dvh);
  /* Over everything a dialog goes over (185) and over the toasts (200) as well:
     this IS the screen while it is up, and a toast drawn on a photograph is
     read as part of the photograph. */
  z-index: 210;
  display: flex;
  flex-direction: column;
  background: rgba(9, 12, 20, 0.94);
}
.lb:focus { outline: none; }

.lb__bar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  padding-top: calc(var(--space-3) + env(safe-area-inset-top));
  color: rgba(255, 255, 255, 0.86);
}

.lb__name {
  flex: 1;
  min-width: 0;
  font-size: var(--text-sm);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lb__count {
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.6);
}

.lb__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: inherit;
  cursor: pointer;
}
.lb__icon:hover { background: rgba(255, 255, 255, 0.2); }

.lb__stage {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-2) var(--space-4) calc(var(--space-4) + env(safe-area-inset-bottom));
}

.lb__media {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.lb__fallback {
  padding: var(--space-3) var(--space-5);
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: var(--text-sm);
  text-decoration: none;
}

.lb__step {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.86);
  cursor: pointer;
}
.lb__step:hover { background: rgba(255, 255, 255, 0.2); }
.lb__step--back { left: var(--space-2); }
.lb__step--on { right: var(--space-2); }

/* On a phone the swipe is the gesture and the arrows are in the way of the
   picture; the count in the bar still says there is more than one. */
@media (max-width: 640px) {
  .lb__step { display: none; }
}

.fade-enter-active, .fade-leave-active { transition: opacity var(--fast); }
.fade-enter-from, .fade-leave-to { opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .fade-enter-active, .fade-leave-active { transition: none; }
}
</style>
